import React from 'react';
import { Typography, Card, Tag, Alert, Button, Skeleton } from 'antd';
import { TrophyOutlined, ReloadOutlined } from '@ant-design/icons';
import { tokens } from '../theme/tokens';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Mismo JWT que el resto de la app (context/social.tsx, mis-publicaciones-page.tsx).
// El backend gatea GET /drupi/my-prizes con require_open_id: la identidad sale del
// token, así que un usuario solo puede ver SUS premios.
const JWT_KEY = 'drupi_social_jwt';
const authHeaders = (): Record<string, string> => {
  const jwt = localStorage.getItem(JWT_KEY);
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

// Premio mensual (milestone) — backend /drupi/my-prizes. Misma entidad que ve la marca
// en brands (/brand-admin/user/{id}/prizes): lo genera el PrizesWorker al cierre de mes.
interface Prize {
  year: number;
  month: number;
  points: number;
  milestone: number;
  prize_usd: number;
  status: string;
  created_at: string;
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// Estados del premio: 'pending' (lo genera el worker) y 'paid' (lo marca el panel de
// Brands al ejecutar los pagos). Cualquier estado desconocido se muestra crudo en el
// estilo neutro. Look "metal pulido" (feedback 2026-07-31: nada de amarillo): el tag
// pendiente va plateado sobre la card oscura; el pagado, en verde para el contraste.
const STATUS_TAG: Record<string, { style: React.CSSProperties; label: string }> = {
  pending: {
    label: 'A cobrar',
    style: { background: 'rgba(255,255,255,0.08)', color: '#dfe3ea', borderColor: 'rgba(255,255,255,0.28)' },
  },
  paid: {
    label: 'Pagado',
    style: { background: 'rgba(82,196,26,0.15)', color: '#95de64', borderColor: 'rgba(82,196,26,0.45)' },
  },
};

// Texto "plateado": gradiente claro→gris clipeado al texto (el color de relleno es
// transparente y se ve el gradiente — efecto metal).
const silverText: React.CSSProperties = {
  background: 'linear-gradient(180deg, #fafbfc 0%, #c7ccd6 55%, #98a1b3 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

// Card "metálica" oscura (grafito con leve degradado) — reemplaza el look dorado.
const metalCard: React.CSSProperties = {
  borderRadius: 14,
  background: 'linear-gradient(135deg, #16161d 0%, #24242f 55%, #121218 100%)',
  border: '1px solid rgba(255,255,255,0.14)',
  boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
};

export const MisPremiosPage: React.FC = () => {
  const [prizes, setPrizes] = React.useState<Prize[] | null>(null);
  const [error, setError] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    setPrizes(null);
    setError(false);
    fetch(`${API_BASE}/drupi/my-prizes`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { if (alive) setPrizes(Array.isArray(d?.prizes) ? d.prizes : []); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [reloadKey]);

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 24px' }}>
      {/* Page title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <TrophyOutlined style={{ fontSize: 22, color: tokens.colors.brand.blue }} />
        <Title level={3} style={{ margin: 0, color: tokens.colors.primary.neutral[900], fontWeight: 700 }}>
          Mis Premios
        </Title>
      </div>

      {/* Error visible con retry — nada de fallar en silencio. */}
      {error && (
        <Alert
          type='error'
          showIcon
          message='No pudimos cargar tus premios'
          description='Hubo un problema al consultar tus premios. Probá de nuevo.'
          action={
            <Button size='small' icon={<ReloadOutlined />} onClick={() => setReloadKey(k => k + 1)}>
              Reintentar
            </Button>
          }
          style={{ borderRadius: 12 }}
        />
      )}

      {/* Cargando */}
      {!error && prizes === null && (
        <Card style={{ borderRadius: 14 }} styles={{ body: { padding: 24 } }}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </Card>
      )}

      {/* Sin premios todavía — mismo look metálico que las cards de premio. */}
      {!error && prizes !== null && prizes.length === 0 && (
        <Card
          className='prize-card'
          style={{ ...metalCard, borderRadius: 16, textAlign: 'center' }}
          styles={{ body: { padding: '32px 24px' } }}
        >
          <div className='prize-shine' />
          <div style={{ fontSize: 34, lineHeight: 1 }}>🏆</div>
          <Text style={{ display: 'block', marginTop: 10, fontSize: 16, fontWeight: 800, ...silverText }}>
            Todavía no tenés premios
          </Text>
          <Text style={{ display: 'block', marginTop: 6, fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
            Los premios se generan al cierre de cada mes según tus puntos:
            cada 100.000 puntos son US$100, con un tope de US$500 por mes.
            Sumá puntos amplificando videos y volvé a fin de mes.
          </Text>
        </Card>
      )}

      {/* Aviso 72 h: si algún premio figura "Pagado", el depósito puede no haber
          impactado todavía — que el usuario no se asuste ni escriba a soporte
          (pedido 2026-07-31). */}
      {!error && prizes !== null && prizes.some(p => p.status === 'paid') && (
        <Alert
          type='info'
          showIcon
          message='Los pagos pueden demorar en acreditarse'
          description='Un premio marcado como "Pagado" puede tardar hasta 72 horas en impactar en tu cuenta, según tu banco o billetera.'
          style={{ borderRadius: 12, marginBottom: 14 }}
        />
      )}

      {/* Lista de premios (newest-first, como los devuelve el backend) */}
      {!error && prizes !== null && prizes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {prizes.map(p => {
            const tag = STATUS_TAG[p.status] || { style: STATUS_TAG.pending.style, label: p.status };
            return (
              <Card
                key={`${p.year}-${p.month}`}
                className='prize-card'
                style={metalCard}
                styles={{ body: { padding: '18px 22px' } }}
              >
                {/* Franja de luz que barre la card al hover/tap (ver index.css). */}
                <div className='prize-shine' />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', display: 'block', letterSpacing: 0.4 }}>
                      {MESES[p.month - 1]} {p.year}
                    </Text>
                    <Text style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.25, ...silverText }}>
                      US${p.prize_usd}
                    </Text>
                  </div>
                  <Tag style={{ fontWeight: 600, marginRight: 0, ...tag.style }}>
                    {tag.label}
                  </Tag>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
